import { TodayStep } from '../components/dashboard/TodayStepCard';
import { PrivateProfileService } from './privateProfileService';
import { BookRepository } from '../repositories/bookRepository';
import { BookService } from './bookService';
import { InsightRotationService } from './insightRotationService';
import { AIInsightRecommendation } from '../models/PrivateProfile';

/**
 * Service for managing daily insight rotation
 * 
 * FEATURES:
 * - Daily rotation through insight plan
 * - Skips insights already shown today
 * - Skips completed insights
 * - Automatically fetches more insights when plan is exhausted
 * - Marks insights as shown to prevent duplicates
 */

class TodayStepService {
  /**
   * Get today's step for a user with intelligent rotation
   * 
   * Logic:
   * 1. Get today's date (YYYY-MM-DD)
   * 2. Check if an insight was already shown TODAY - if yes, return it (consistency)
   * 3. Otherwise, find next unshown/uncompleted insight
   * 4. If all exhausted, fetch more insights
   * 5. Mark selected insight with today's date
   * 6. Return insight
   */
  async getTodayStep(userId: string, languageCode: string = 'en'): Promise<TodayStep | null> {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      
      // Get user's profile
      const profileResult = await PrivateProfileService.getProfile(userId);
      
      if (!profileResult.success || !profileResult.data) {
        console.warn('❌ No profile found for user');
        return null;
      }

      const profile = profileResult.data;
      const recommendations = profile.currentRecommendations;
      
      if (!recommendations) {
        console.warn('❌ No currentRecommendations in profile');
        return null;
      }
      
      // Get insights from the plan
      let insights = recommendations.insightPlan?.insights || [];
      
      if (insights.length === 0) {
        console.warn('❌ No insights in recommendation plan');
        return null;
      }

      console.log(`📅 Finding today's insight (${today})...`);
      console.log(`📚 Total insights in plan: ${insights.length}`);

      // STEP 1: Check if we already showed an insight TODAY
      // If yes, return the same one (consistency within the day)
      const todaysInsight = insights.find(insight => {
        const progress = profile.insightsProgress[insight.insightId];
        return progress?.lastShownAsInsightOfDayDate === today;
      });

      if (todaysInsight) {
        console.log(`🔄 Returning same insight shown earlier today: ${todaysInsight.insightId}`);
        return await this.formatTodayStep(todaysInsight, languageCode);
      }

      // STEP 2: Find next unshown/uncompleted insight
      // "Unshown" = never shown OR last shown on a previous day
      const availableInsights = insights.filter(insight => {
        const progress = profile.insightsProgress[insight.insightId];
        
        // Skip completed insights
        if (progress?.isCompleted === true) {
          console.log(`✅ Skipping ${insight.insightId}: completed`);
          return false;
        }
        
        // Show if never shown OR last shown on a different day (not today)
        const lastShown = progress?.lastShownAsInsightOfDayDate;
        const isAvailable = !lastShown || lastShown < today;
        
        if (!isAvailable) {
          console.log(`⏭️ Skipping ${insight.insightId}: shown on ${lastShown}`);
        }
        
        return isAvailable;
      });

      console.log(`🎯 Available insights: ${availableInsights.length}`);

      let selectedInsight: AIInsightRecommendation;

      // STEP 3: If no available insights, fetch more
      if (availableInsights.length === 0) {
        console.log('🔄 All insights shown/completed, fetching more...');
        
        const existingInsightIds = insights.map(i => i.insightId);
        const additionalInsights = await InsightRotationService.fetchAdditionalInsights(
          userId,
          existingInsightIds,
          languageCode,
          5 // Fetch 5 new insights
        );

        if (additionalInsights.length === 0) {
          console.warn('⚠️ Could not fetch additional insights');
          return null;
        }

        console.log(`✅ Fetched ${additionalInsights.length} new insights`);

        // Add new insights to the plan
        const updatedInsights = [...insights, ...additionalInsights];
        
        // Update the profile with new insights
        await PrivateProfileService.appendInsightsToCurrentPlan(
          userId,
          additionalInsights,
          recommendations.insightPlan?.title || 'Your Daily Steps'
        );

        // Select the first new insight
        selectedInsight = additionalInsights[0];
      } else {
        // Select the first available insight
        selectedInsight = availableInsights[0];
      }

      console.log(`🎯 Selected insight: ${selectedInsight.insightId}`);

      // Mark as shown today (only for newly selected insights, not already-shown-today ones)
      await PrivateProfileService.markInsightAsShownToday(
        userId,
        selectedInsight.insightId,
        selectedInsight.bookId || 'unknown'
      );

      console.log(`✅ Today's step selected and marked: ${selectedInsight.insightId}`);

      // Format and return
      return await this.formatTodayStep(selectedInsight, languageCode);
    } catch (error) {
      console.error('❌ Error fetching today\'s step:', error);
      return null;
    }
  }

  /**
   * Helper: Format an AIInsightRecommendation as a TodayStep
   */
  private async formatTodayStep(
    insight: AIInsightRecommendation,
    languageCode: string = 'en'
  ): Promise<TodayStep | null> {
    try {
      let bookId = insight.bookId;
      let insightDetails;
      
      // If no bookId provided, fetch insight directly to get it
      if (!bookId) {
        console.log('📍 No bookId in recommendation, fetching insight to get it...');
        const directInsight = await BookRepository.getInsightById(insight.insightId, languageCode);
        if (!directInsight) {
          console.error('❌ Could not fetch insight:', insight.insightId);
          return null;
        }
        insightDetails = directInsight;
        bookId = directInsight.bookId;
      }
      
      // Fetch book + all insights (uses cache)
      const bookResult = await BookService.getBookDetail(bookId, languageCode);
      
      if (!bookResult.success || !bookResult.data) {
        console.error('❌ Could not fetch book details for:', bookId);
        return null;
      }
      
      const { book, insights: allInsights } = bookResult.data;
      
      // Find the specific insight (if we didn't fetch it already)
      if (!insightDetails) {
        insightDetails = allInsights.find(i => i.id === insight.insightId);
        if (!insightDetails) {
          console.error('❌ Insight not found in book:', insight.insightId);
          return null;
        }
      }

      // Map to TodayStep format (using book data we already fetched)
      return {
        insightId: insight.insightId,
        bookId: bookId,
        outcome: insight.reason,
        action: insightDetails.title,
        bookTitle: book.title,
        bookAuthor: book.author,
      };
    } catch (error) {
      console.error('❌ Error formatting today\'s step:', error);
      return null;
    }
  }

  /**
   * Mark today's step as started (user clicked to read the full insight)
   */
  async markStepStarted(userId: string, insightId?: string, bookId?: string): Promise<boolean> {
    try {
      if (!insightId) {
        console.warn('⚠️ No insightId provided to markStepStarted');
        return false;
      }

      // Update insight progress to mark as started
      const result = await PrivateProfileService.updateUserInsightProgress(
        userId,
        insightId,
        bookId,
        {
          lastAccessedAt: Date.now() / 1000,
        }
      );

      if (result.success) {
        console.log(`✅ User ${userId} started insight ${insightId}`);
        return true;
      } else {
        console.error(`❌ Failed to mark insight as started: ${result.error}`);
        return false;
      }
    } catch (error) {
      console.error('❌ Error marking step as started:', error);
      return false;
    }
  }

  /**
   * Mark today's step as completed (user finished reading)
   */
  async markStepCompleted(userId: string, insightId?: string, bookId?: string): Promise<boolean> {
    try {
      if (!insightId) {
        console.warn('⚠️ No insightId provided to markStepCompleted');
        return false;
      }

      // Update insight progress to mark as completed
      const result = await PrivateProfileService.updateUserInsightProgress(
        userId,
        insightId,
        bookId,
        {
          isCompleted: true,
          completedAt: Date.now() / 1000,
        }
      );

      if (result.success) {
        console.log(`✅ User ${userId} completed insight ${insightId}`);
        return true;
      } else {
        console.error(`❌ Failed to mark insight as completed: ${result.error}`);
        return false;
      }
    } catch (error) {
      console.error('❌ Error marking step as completed:', error);
      return false;
    }
  }
}

export const todayStepService = new TodayStepService();

